// Подготовка скриншота перед отправкой на распознавание. Чистые функции
// (валидация, расчёт размера, счёт байт) не трогают DOM и тестируются
// напрямую; DOM нужен только prepareImage() для реального уменьшения через
// canvas — она деградирует до «отдать файл как есть», если canvas недоступен.

/** Предел исходного файла — тело запроса и трафик реальны, поэтому режем на входе. */
export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
/** Длинная сторона после уменьшения: скриншот выдачи читается и при таком размере. */
const MAX_SIDE_PX = 1600;
const JPEG_QUALITY = 0.85;
/** Форматы, которые сервер принимает как есть, без пережатия. */
const ACCEPTED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

/**
 * Сколько скриншотов уходит за один запрос. Дублирует MAX_IMAGES из
 * server/ai/recognize.mjs на случай, если конфиг ещё не приехал; пришедший с
 * сервера сильнее.
 */
export const MAX_IMAGES = 7;
/** Тот же запасной вариант для суммарного веса пачки (MAX_TOTAL_IMAGE_BYTES). */
export const MAX_TOTAL_BYTES = 10 * 1024 * 1024;

export interface ImagePrepareError {
  /** Ключ i18n: recog.shot.badType | recog.shot.tooBig. */
  key: 'recog.shot.badType' | 'recog.shot.tooBig';
  params?: Record<string, string | number>;
}

export type ImagePrepareResult =
  | { ok: true; dataUrl: string; bytes: number }
  | { ok: false; error: ImagePrepareError };

/** Человекочитаемая подпись размера для recog.shot.ready — '512 KB', '2.3 MB'. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  // Меньше десяти — один знак после запятой, иначе смысла в дроби нет.
  const digits = value < 10 ? 1 : 0;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
}

/**
 * Тип и размер файла до какой-либо обработки. Принимает форму File — тестам не
 * нужен настоящий File.
 *
 * Проверяется именно «картинка вообще», а не белый список: HEIC с айфона, gif и
 * avif браузер прекрасно рисует на canvas, и после пережатия в JPEG сервер их
 * принимает. Отказывать им на входе значило бы отказывать в файлах, которые
 * отлично разбираются (см. prepareImage — если пережать не вышло, отказ вернётся
 * там).
 */
export function validateImageFile(file: { type: string; size: number }): ImagePrepareError | null {
  if (!/^image\//i.test(file.type)) {
    return { key: 'recog.shot.badType' };
  }
  if (file.size > MAX_SOURCE_BYTES) {
    return { key: 'recog.shot.tooBig', params: { limit: Math.round(MAX_SOURCE_BYTES / (1024 * 1024)) } };
  }
  return null;
}

/** Целевой размер: длинная сторона не больше MAX_SIDE_PX, пропорции сохраняются. */
export function computeTargetSize(width: number, height: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= MAX_SIDE_PX || longest === 0) return { width, height };
  const scale = MAX_SIDE_PX / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/** Размер данных data URL в байтах — по длине base64-части, с учётом паддинга. */
export function dataUrlBytes(dataUrl: string): number {
  const commaIdx = dataUrl.indexOf(',');
  const base64 = commaIdx === -1 ? dataUrl : dataUrl.slice(commaIdx + 1);
  const paddingMatch = /=+$/.exec(base64);
  const padding = paddingMatch ? paddingMatch[0].length : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = dataUrl;
  });
}

/**
 * Готовит картинку к отправке: проверяет тип/размер, затем пробует уменьшить
 * через canvas в JPEG (это и снижает трафик, и укладывается в лимит тела
 * запроса). Поддержка canvas проверяется ДО попытки декодировать саму
 * картинку — иначе в средах без реального декодера (headless-тесты) промис
 * загрузки Image никогда бы не разрешился.
 *
 * Пережатие для форматов вне белого списка (HEIC, gif, avif) не «приятный
 * бонус», а единственный путь: как есть сервер их не примет. Поэтому если
 * canvas недоступен или картинка не декодировалась, оригинал отдаётся только
 * из белого списка, а остальное честно отклоняется здесь.
 */
export async function prepareImage(file: File): Promise<ImagePrepareResult> {
  const validationError = validateImageFile(file);
  if (validationError) return { ok: false, error: validationError };

  const sendableAsIs = ACCEPTED_TYPES.has(file.type);
  const badType: ImagePrepareResult = { ok: false, error: { key: 'recog.shot.badType' } };

  const ctx = getCanvas2dContext();
  if (!ctx) {
    if (!sendableAsIs) return badType;
    return { ok: true, dataUrl: await readFileAsDataUrl(file), bytes: file.size };
  }

  const original = await readFileAsDataUrl(file);
  try {
    const img = await loadImageElement(original);
    const { width, height } = computeTargetSize(img.naturalWidth || img.width, img.naturalHeight || img.height);
    ctx.canvas.width = width;
    ctx.canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    const resized = ctx.canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    return { ok: true, dataUrl: resized, bytes: dataUrlBytes(resized) };
  } catch {
    // Декодирование не удалось (битый файл, формат, который этот браузер не
    // умеет) — из белого списка отправляем оригинал, остальное отклоняем.
    return sendableAsIs ? { ok: true, dataUrl: original, bytes: file.size } : badType;
  }
}

/** null, если canvas или его 2D-контекст недоступны (старый браузер, тестовая среда без canvas-пакета). */
function getCanvas2dContext(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') return null;
  try {
    const canvas = document.createElement('canvas');
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}
